// Builds a real PDF from the recipe page, block by block, so a step is never
// cut across pages. Letter portrait, paper-toned, footer with URL + date on
// every page. Blocks are any elements carrying [data-pdf].
// Libraries load on demand so the reading experience never pays for them.

const PAGE_W = 612; // letter, pt
const PAGE_H = 792;
const MARGIN = 42;
const FOOTER_H = 26;
const CONTENT_W = PAGE_W - MARGIN * 2;
const CONTENT_H = PAGE_H - MARGIN * 2 - FOOTER_H;
const GAP = 14; // pt between blocks

export async function downloadRecipePdf(root: HTMLElement, slug: string) {
  const blocks = [...root.querySelectorAll<HTMLElement>('[data-pdf]')];
  if (!blocks.length) return;

  const [{ jsPDF }, { default: html2canvas }] = await Promise.all([
    import('jspdf'),
    import('html2canvas-pro'),
  ]);

  const pdf = new jsPDF({ unit: 'pt', format: 'letter' });
  const pageBg = '#faf6ef';
  let y = MARGIN;
  let page = 1;

  const paintBg = () => {
    pdf.setFillColor(pageBg);
    pdf.rect(0, 0, PAGE_W, PAGE_H, 'F');
  };
  const footer = () => {
    const line = `${window.location.href}  ·  ${new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })}`;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor('#6e6457');
    pdf.text(line, PAGE_W / 2, PAGE_H - MARGIN / 2, { align: 'center' });
  };

  paintBg();

  for (const el of blocks) {
    let canvas: HTMLCanvasElement;
    try {
      canvas = await html2canvas(el, {
        scale: 2,
        backgroundColor: null,
        logging: false,
      });
    } catch (err) {
      console.error('pdf: block failed', blocks.indexOf(el), el.className, err);
      throw err;
    }
    if (!canvas.width || !canvas.height) {
      console.error('pdf: empty canvas for block', blocks.indexOf(el), el.className);
      continue;
    }
    const ratio = CONTENT_W / canvas.width;
    const hPt = canvas.height * ratio;

    if (hPt <= CONTENT_H) {
      if (y + hPt > MARGIN + CONTENT_H) {
        footer();
        pdf.addPage();
        page += 1;
        paintBg();
        y = MARGIN;
      }
      pdf.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', MARGIN, y, CONTENT_W, hPt);
      y += hPt + GAP;
    } else {
      // Block taller than a page (rare): start it on a fresh page and slice.
      if (y > MARGIN) {
        footer();
        pdf.addPage();
        page += 1;
        paintBg();
        y = MARGIN;
      }
      const slicePx = CONTENT_H / ratio;
      let offset = 0;
      while (offset < canvas.height) {
        const h = Math.min(slicePx, canvas.height - offset);
        const part = document.createElement('canvas');
        part.width = canvas.width;
        part.height = h;
        part.getContext('2d')!.drawImage(canvas, 0, offset, canvas.width, h, 0, 0, canvas.width, h);
        if (offset > 0) {
          footer();
          pdf.addPage();
          page += 1;
          paintBg();
        }
        pdf.addImage(part.toDataURL('image/jpeg', 0.92), 'JPEG', MARGIN, MARGIN, CONTENT_W, h * ratio);
        offset += h;
      }
      y = MARGIN + ((canvas.height - (canvas.height % slicePx || slicePx)) % slicePx) * ratio;
      y = MARGIN + CONTENT_H; // force next block onto a new page
    }
  }
  footer();
  void page;
  pdf.save(`cookbook-${slug}.pdf`);
}
